import {
  Box,
  Disclaimer,
  displayCentsInDollars,
  Div,
  H5,
  lightTheme,
  LocationServiceValueBrand,
} from '@vgrubs/components';
import { countOf } from '../helpers/serviceValueLines';
import {
  partColor,
  partSegments,
  partsTotal,
  valueParts,
} from '../helpers/valueParts';
import {
  BrandName,
  PartDot,
  PartItem,
  PartLegend,
} from './BrandValueCard.styled';
import { ValueBar } from './ValueBar';

type Props = {
  brand: LocationServiceValueBrand;
  /** The client's whole total, for the brand's share of it. */
  clientTotal: number;
  /** The biggest brand's total: a full bar. */
  scale: number;
};

/**
 * One brand: what it made and saved in all, its share of the client's total,
 * a bar of what that is made of, and each part with what it stands on.
 *
 * Only our virtual brands have sales and a payout. The location's own brand
 * (or an RB brand) simply has no payout part and no sales line, rather than
 * a $0 that reads as "sold nothing" - and no tag either: the team knows its
 * own brands, the backend's flag only decides what applies. A part shows when it has an amount or anything behind it -
 * 46 deliveries that saved $0 are still worth seeing.
 */
export const BrandValueCard = ({ brand, clientTotal, scale }: Props) => {
  const parts = valueParts(brand, { withPayout: brand.virtual });
  const total = partsTotal(parts);
  const shown = parts.filter((part) => part.amount !== 0 || part.count > 0);
  const share = clientTotal > 0 ? (total / clientTotal) * 100 : 0;
  // A few cents of a large total round to 0%, which reads as nothing at all.
  const shareLabel = share > 0 && share < 1 ? '<1' : Math.round(share);

  return (
    <Box $boxNoShadow $p="16px" $pSM="12px 16px" $minHeight="0">
      <Div $flex $between $alignStart $gap="16px">
        <Div $minWidth="0">
          <BrandName>{brand.brandName}</BrandName>
          {/* Sales only exist for our virtual brands; the location's own
              brand just goes without the line. */}
          {brand.virtual && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              {`${countOf(brand.orders, 'order')} · ${displayCentsInDollars(
                brand.sales
              )} sales`}
            </Disclaimer>
          )}
        </Div>

        <Div $flexNone $textAlignRight>
          <H5 $m="0">{displayCentsInDollars(total)}</H5>
          {clientTotal > 0 && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              {shareLabel}% of total
            </Disclaimer>
          )}
        </Div>
      </Div>

      <Div $mt="12">
        <ValueBar segments={partSegments(parts)} scale={scale} height={6} />
      </Div>

      {shown.length > 0 ? (
        <PartLegend>
          {shown.map((part) => (
            <PartItem key={part.key}>
              <PartDot $color={partColor(part.key)} />
              <Disclaimer $m="0" $color={lightTheme.darkGray} $noWrap>
                {part.label} <b>{displayCentsInDollars(part.amount)}</b>
              </Disclaimer>
              <Disclaimer $m="0" $color={lightTheme.lightGray} $noWrap>
                {part.countLabel}
              </Disclaimer>
            </PartItem>
          ))}
        </PartLegend>
      ) : (
        <Disclaimer $m="10px 0 0" $color={lightTheme.lightGray}>
          Nothing made or saved in this period.
        </Disclaimer>
      )}
    </Box>
  );
};
